import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useMemo, useState } from 'react';
import { LookupKey, lookupApis } from '../admin.api';
import ResourceManager, { ResourceColumn, ResourceField } from '../components/ResourceManager';

interface LookupConfig {
  key: LookupKey;
  title: string;
  entityName: string;
  columns: ResourceColumn[];
  fields: ResourceField[];
}

const activeCell = (value: unknown) => (value === false ? 'No' : 'Yes');

/** `code` + `label` + description/active/sort — the shape eight of the ten tables share. */
const codeLabelColumns = (labelHeader = 'Label'): ResourceColumn[] => [
  { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
  { id: 'code', dataKey: 'code', label: 'Code', align: 'left', sortable: true },
  { id: 'label', dataKey: 'label', label: labelHeader, align: 'left', sortable: true },
  { id: 'description', dataKey: 'description', label: 'Description', align: 'left' },
  { id: 'sortOrder', dataKey: 'sortOrder', label: 'Order', align: 'center', sortable: true },
  { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
];

const codeLabelFields: ResourceField[] = [
  { name: 'code', label: 'Code', required: true, helperText: 'Unique machine key, e.g. new_job' },
  { name: 'label', label: 'Label', required: true },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
];

const yesNoCell = (value: unknown) => (value ? 'Yes' : 'No');

const LOOKUPS: LookupConfig[] = [
  {
    key: 'workActivities',
    title: 'WORK ACTIVITIES',
    entityName: 'Work Activity',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'name', dataKey: 'name', label: 'Work Activity Name', align: 'left', sortable: true },
      { id: 'description', dataKey: 'description', label: 'Description', align: 'left' },
      { id: 'sortOrder', dataKey: 'sortOrder', label: 'Order', align: 'center', sortable: true },
      { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
    ],
    fields: [
      { name: 'name', label: 'Work Activity Name', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'sort_order', label: 'Sort Order', type: 'number' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'timeEntryCategories',
    title: 'TIME ENTRY CATEGORIES',
    entityName: 'Time Entry Category',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'name', dataKey: 'name', label: 'Category', align: 'left', sortable: true },
      { id: 'description', dataKey: 'description', label: 'Description', align: 'left' },
      { id: 'sortOrder', dataKey: 'sortOrder', label: 'Order', align: 'center', sortable: true },
      { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
    ],
    fields: [
      { name: 'name', label: 'Category', required: true, helperText: 'Work outside a task, e.g. Meeting or Training.' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'sort_order', label: 'Sort Order', type: 'number' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'typeOfWorkActivities',
    title: 'TYPES OF WORK ACTIVITY',
    entityName: 'Type of Work Activity',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'workActivityName', dataKey: 'workActivityName', label: 'Work Activity', align: 'left', sortable: true },
      { id: 'name', dataKey: 'name', label: 'Type of Work Activity', align: 'left', sortable: true },
      {
        id: 'requiresStructuralDrawing',
        dataKey: 'requiresStructuralDrawing',
        label: 'Requires Structural Drawing',
        align: 'center',
        sortable: true,
        renderCell: yesNoCell,
      },
      {
        id: 'requiresShutteringFraming',
        dataKey: 'requiresShutteringFraming',
        label: 'Requires Shuttering / Framing',
        align: 'center',
        sortable: true,
        renderCell: yesNoCell,
      },
      { id: 'sortOrder', dataKey: 'sortOrder', label: 'Order', align: 'center', sortable: true },
      { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
    ],
    fields: [
      {
        name: 'work_activity_id',
        label: 'Work Activity',
        type: 'autocomplete',
        required: true,
        allowCustomValue: false,
        loadOptions: async () =>
          (await lookupApis.workActivities.list()).map((a: any) => ({ label: a.name, value: a.id })),
      },
      { name: 'name', label: 'Type of Work Activity', required: true },
      { name: 'requires_structural_drawing', label: 'Requires structural drawing', type: 'boolean', defaultValue: false },
      { name: 'requires_shuttering_framing', label: 'Requires shuttering / framing', type: 'boolean', defaultValue: false },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'sort_order', label: 'Sort Order', type: 'number' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'projectTypes',
    title: 'PROJECT TYPES',
    entityName: 'Project Type',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'constructionWorkTypes',
    title: 'CONSTRUCTION WORK TYPES',
    entityName: 'Construction Work Type',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'employmentTypes',
    title: 'EMPLOYMENT TYPES',
    entityName: 'Employment Type',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'workArrangements',
    title: 'WORK ARRANGEMENTS',
    entityName: 'Work Arrangement',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'qualifications',
    title: 'QUALIFICATIONS',
    entityName: 'Qualification',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'tools',
    title: 'TOOLS',
    entityName: 'Tool',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'buildingTypes',
    title: 'BUILDING TYPES',
    entityName: 'Building Type',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'localities',
    title: 'LOCALITIES',
    entityName: 'Locality',
    columns: codeLabelColumns('Locality'),
    // `code` is derived from the name server-side when left blank, so it is optional here
    // — the same row can be created from the job screen's "+ Add", which asks for a name only.
    fields: [
      { name: 'label', label: 'Locality Name', required: true },
      { name: 'code', label: 'Code', helperText: 'Leave blank to derive it from the name' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'sort_order', label: 'Sort Order', type: 'number' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'floorTypes',
    title: 'FLOOR TYPES',
    entityName: 'Floor Type',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'documentCategories',
    title: 'DOCUMENT CATEGORIES',
    entityName: 'Document Category',
    columns: codeLabelColumns(),
    fields: codeLabelFields,
  },
  {
    key: 'priorities',
    title: 'PRIORITIES',
    entityName: 'Priority',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'code', dataKey: 'code', label: 'Code', align: 'left', sortable: true },
      { id: 'label', dataKey: 'label', label: 'Label', align: 'left', sortable: true },
      { id: 'level', dataKey: 'level', label: 'Level', align: 'center', sortable: true },
      { id: 'colorHex', dataKey: 'colorHex', label: 'Colour', align: 'center' },
      { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
    ],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'level', label: 'Level', type: 'number' },
      { name: 'color_hex', label: 'Colour (hex)', helperText: '#EF4444' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'shiftTypes',
    title: 'SHIFT TYPES',
    entityName: 'Shift Type',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'code', dataKey: 'code', label: 'Code', align: 'left', sortable: true },
      { id: 'label', dataKey: 'label', label: 'Label', align: 'left', sortable: true },
      { id: 'startTime', dataKey: 'startTime', label: 'Start', align: 'center' },
      { id: 'endTime', dataKey: 'endTime', label: 'End', align: 'center' },
      { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
    ],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'start_time', label: 'Start Time', type: 'time', helperText: 'HH:mm:ss' },
      { name: 'end_time', label: 'End Time', type: 'time', helperText: 'HH:mm:ss' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'sanctioningAuthorities',
    title: 'SANCTIONING AUTHORITIES',
    entityName: 'Authority',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'code', dataKey: 'code', label: 'Code', align: 'left', sortable: true },
      { id: 'name', dataKey: 'name', label: 'Name', align: 'left', sortable: true },
      { id: 'shortName', dataKey: 'shortName', label: 'Short Name', align: 'left' },
      { id: 'city', dataKey: 'city', label: 'City', align: 'left' },
      { id: 'state', dataKey: 'state', label: 'State', align: 'left' },
      { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
    ],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'short_name', label: 'Short Name' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'statusCategories',
    title: 'STATUS CATEGORIES',
    entityName: 'Status Category',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'code', dataKey: 'code', label: 'Code', align: 'left', sortable: true },
      { id: 'label', dataKey: 'label', label: 'Label', align: 'left', sortable: true },
      { id: 'description', dataKey: 'description', label: 'Description', align: 'left' },
    ],
    fields: [
      { name: 'code', label: 'Code', required: true, helperText: 'e.g. work_job, job_task' },
      { name: 'label', label: 'Label', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
    ],
  },
  {
    key: 'statuses',
    title: 'STATUSES',
    entityName: 'Status',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'code', dataKey: 'code', label: 'Code', align: 'left', sortable: true },
      { id: 'label', dataKey: 'label', label: 'Label', align: 'left', sortable: true },
      { id: 'colorHex', dataKey: 'colorHex', label: 'Colour', align: 'center' },
      { id: 'sortOrder', dataKey: 'sortOrder', label: 'Order', align: 'center', sortable: true },
      {
        id: 'isTerminal',
        dataKey: 'isTerminal',
        label: 'Terminal',
        align: 'center',
        renderCell: (v: unknown) => (v ? 'Yes' : 'No'),
      },
      {
        id: 'isDefault',
        dataKey: 'isDefault',
        label: 'Default',
        align: 'center',
        renderCell: (v: unknown) => (v ? 'Yes' : 'No'),
      },
    ],
    fields: [
      {
        name: 'status_category_id',
        label: 'Status Category ID',
        type: 'number',
        required: true,
        helperText: 'ID from the Status Categories tab',
      },
      { name: 'code', label: 'Code', required: true },
      { name: 'label', label: 'Label', required: true },
      { name: 'color_hex', label: 'Colour (hex)' },
      { name: 'sort_order', label: 'Sort Order', type: 'number' },
      { name: 'is_terminal', label: 'Terminal state', type: 'boolean', defaultValue: false },
      { name: 'is_default', label: 'Default for category', type: 'boolean', defaultValue: false },
    ],
  },
  {
    key: 'financialYears',
    title: 'FINANCIAL YEARS',
    entityName: 'Financial Year',
    columns: [
      { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
      { id: 'label', dataKey: 'label', label: 'Label', align: 'left', sortable: true },
      { id: 'startDate', dataKey: 'startDate', label: 'Start', align: 'center' },
      { id: 'endDate', dataKey: 'endDate', label: 'End', align: 'center' },
      { id: 'isActive', dataKey: 'isActive', label: 'Active', align: 'center', renderCell: activeCell },
    ],
    fields: [
      { name: 'label', label: 'Label', required: true, helperText: 'e.g. 2025-26' },
      { name: 'start_date', label: 'Start Date', type: 'date' },
      { name: 'end_date', label: 'End Date', type: 'date' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: false },
    ],
  },
];

/**
 * One screen over the reference tables `V5__structural_normalization.sql` introduced.
 * Every tab is the same CRUD contract, so they share `ResourceManager`.
 */
export default function ReferenceDataPage() {
  const [tab, setTab] = useState(0);
  const active = LOOKUPS[tab];
  const client = useMemo(() => lookupApis[active.key], [active.key]);

  return (
    <Stack gap={3}>
      <Tabs
        value={tab}
        onChange={(_e, value: number) => setTab(value)}
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
      >
        {LOOKUPS.map((lookup) => (
          <Tab key={lookup.key} label={lookup.entityName} />
        ))}
      </Tabs>

      <ResourceManager
        // Remount on tab change so list state and form defaults reset cleanly.
        key={active.key}
        title={active.title}
        entityName={active.entityName}
        client={client}
        columns={active.columns}
        fields={active.fields}
      />
    </Stack>
  );
}
